import { restClient } from '../../client';
import { SessionFormField, SessionFormFieldError, SessionFormFieldsGroup } from '../../resources';
import { PsychiatryDischargeNoteCourseOfTreatment, TherapyDischargeNoteCourseOfTreatment } from './dischargeNotes';

export const enum MedicalNoteType {
  communicationNote = 'communication_note',
  therapyDischargeNote = 'therapy_discharge_note',
  psychiatryDischargeNote = 'psychiatry_discharge_note',
  claimCorrectionNote = 'claim_correction_note'
}

type AssignToNote<T extends Record<MedicalNoteType, unknown>, P extends MedicalNoteType = MedicalNoteType> = P extends keyof T ? T[P] : never;

type BaseNote = {
  id: string;
  status: 'archived' | 'submitted' | 'incomplete';
  cptCodeIds: string[];
  diagnosisIds: string[];
  submittedAt: string | null;
  additionalDetails?: {
    coderId: string;
    appointmentId: string;
  };
};

type BaseCommunicationNote = {
  noteType: MedicalNoteType.communicationNote;
  riskAssessmentForm: {
    fields: SessionFormField[];
    errors: SessionFormFieldError[];
    note: string;
  };
};

type BaseTherapyDischargeNote = {
  noteType: MedicalNoteType.therapyDischargeNote;
  courseOfTreatment: TherapyDischargeNoteCourseOfTreatment;
};

type BasePsychiatryDischargeNote = {
  noteType: MedicalNoteType.psychiatryDischargeNote;
  courseOfTreatment: PsychiatryDischargeNoteCourseOfTreatment;
};

type BaseClaimCorrectionNote = {
  noteType: MedicalNoteType.claimCorrectionNote;
};

type Note<TMedicalNoteType extends MedicalNoteType> = AssignToNote<{
  [MedicalNoteType.communicationNote]: BaseCommunicationNote;
  [MedicalNoteType.therapyDischargeNote]: BaseTherapyDischargeNote;
  [MedicalNoteType.psychiatryDischargeNote]: BasePsychiatryDischargeNote;
  [MedicalNoteType.claimCorrectionNote]: BaseClaimCorrectionNote;
}, TMedicalNoteType> & BaseNote;

export type MedicalNoteForm = {
  fields: (SessionFormField | SessionFormFieldsGroup)[];
  errors: SessionFormFieldError[];
  note: string;
};

export type MedicalNote<TMedicalNoteType extends MedicalNoteType = MedicalNoteType> = {
  form: MedicalNoteForm;
} & Note<TMedicalNoteType>;

type CommunicationNoteActivityForm = {
  [key: string]: {
    label: string;
    key: string;
    value: string;
  };
};

type DischargeNoteActivityForm = {
  [key: string]: {
    label: string;
    key: string;
    value: string;
  };
};

type ClaimCorrectionActivityForm = {
  [key: string]: {
    label: string;
    key: string;
    value: string;
  };
};

type MedicalNoteActivityForm<TMedicalNoteType extends MedicalNoteType> = AssignToNote<{
  [MedicalNoteType.communicationNote]: CommunicationNoteActivityForm;
  [MedicalNoteType.therapyDischargeNote]: DischargeNoteActivityForm;
  [MedicalNoteType.psychiatryDischargeNote]: DischargeNoteActivityForm;
  [MedicalNoteType.claimCorrectionNote]: ClaimCorrectionActivityForm;
}, TMedicalNoteType>;


export type MedicalNoteActivity<TMedicalNoteType extends MedicalNoteType = MedicalNoteType> = {
  form: MedicalNoteActivityForm<TMedicalNoteType>;
} & Note<TMedicalNoteType>;




export type MedicalNoteResponse<TMedicalNoteType extends MedicalNoteType = MedicalNoteType> = {
  data: MedicalNote<TMedicalNoteType>;
};

export const getMedicalNote = async <TMedicalNoteType extends MedicalNoteType = MedicalNoteType>(noteId: string) =>
  restClient.request<MedicalNoteResponse<TMedicalNoteType>>(`/api/v1/medical_notes/${noteId}`);

export type MedicalNoteReviewResponse<TMedicalNoteType extends MedicalNoteType = MedicalNoteType> = {
  data: MedicalNoteActivity<TMedicalNoteType>;
};

export const getMedicalNoteReview = async <TMedicalNoteType extends MedicalNoteType = MedicalNoteType>(noteId: string) =>
  restClient.request<MedicalNoteReviewResponse<TMedicalNoteType>>(`/api/v1/medical_notes/${noteId}`, { params: { as_review: true } });
